import {
  CapTableTransaction,
  BusinessEntity,
  Currency,
  Director,
  OwnershipCapitalContribution,
  Stakeholder,
  Stakeholding,
  StockClass,
} from '@prisma/client';

export enum AuthorizedSharesType {
  NOT_APPLICABLE = 'Not Applicable',
  UNLIMITED = 'Unlimited',
  CUSTOM = 'Custom',
  DEFAULT = 'Default',
}

export type StockClassAdditionalInformation = {
  authorizedSharesType?: AuthorizedSharesType;

  authorizedShares: number;

  // Default prefix for certificate numbers in certificated shares including dash (e.g. CS- in CS-1).
  defaultIdPrefix?: string;

  // All the dates have a type representation of an ISO-8601 date, e.g. 2022-01-28

  // Date on which the board approved the stock class
  boardApprovalDate?: string;

  // Date on which the stockholders approved the stock class
  stockholderApprovalDate?: string;

  // The number of votes each share of this stock class gets
  votesPerShare?: number;

  // Per-share par value of this stock class
  parValue?: TMoney;

  // Per-share price this stock class was issued for
  pricePerShare?: TMoney;

  // Seniority of the stock - determines repayment priority.
  // Seniority is ordered by increasing number so that stock classes with a higher seniority have higher repayment priority
  seniority?: number;

  // The liquidation preference per share for this stock class
  liquidationPreferenceMultiple?: number;

  // The participation cap multiple per share for this stock class
  participationCapMultiple?: number;
};

export type TMoney = {
  amount: number;
  currency: Currency;
};

// Transaction Types

export type TUpsetSingleTransactionFromMultipleTransactionsParams = {
  transactionId?: string;
  directorId?: number;
  corporationId?: number;
  quantity: number;
  stakeholderId?: string;
};

export type TUpsertMultipleTransactionsParams = {
  stockClassId: string;
  corporationIdToBuySharesIn: number;
  transactions: TUpsetSingleTransactionFromMultipleTransactionsParams[];
};

export type TDeleteMultipleCapTableTransactionsParams = {
  capTableTransactionIdsToDelete: string[];
  corporationIdToDeleteSharesIn: number;
};

export type SerializedCapTableTransactionType = CapTableTransaction & {
  stakeholding: Stakeholding & {
    stakeholder: Stakeholder & {
      corporation: BusinessEntity;
      director: Director;
    };
  };
  stockClass: StockClass;
  additionalInformation: {
    quantity: number;
    sharePrice: {
      amount: number;
      currency: Currency;
    };
  };
};

export type StakeholdersDataType = Stakeholder & {
  transactionAdditionalInformation: { numberOfShares: number; price: number };
  director: Director;
  corporation: BusinessEntity;
};

export enum VotingRightsType {
  PROPORTIONAL = 'PROPORTIONAL',
  PER_CAPITA = 'PER_CAPITA',
  UNIT_BASED = 'UNIT_BASED',
  CLASS_BASED = 'CLASS_BASED',
  CUSTOM = 'CUSTOM',
}

export enum GetStakeholdingsByCorporationIdQueryExtensibleFields {
  CAPITAL_CONTRIBUTIONS = 'CAPITAL_CONTRIBUTIONS',
}

export type TStakeholding = Stakeholding & {
  stakeholder: Stakeholder & {
    corporation: BusinessEntity;
    director: Director;
    additionalInformation: {
      primaryContact: {
        name: string;
        emails?: string[];
      };
    };
  };
  capitalContributions: OwnershipCapitalContribution[];
  lossContributionPercentage: number;
  profitContributionPercentage: number;
  votingRights: {
    value: number;
    type: VotingRightsType;
    descriptionOfRights: string;
  };
  membershipInterest: { percentage: number };
};

export type SerializedStockClass = {
  amountRaised: number;
  authorizedShares: number;
  authorizedSharesType: string;
  id: string;
  name: string;
  outstandingShares: number;
  type: string;
  additionalInformation: StockClassAdditionalInformation;
};
